/**
 * 工程工作台的紧凑阶段条。
 * 原来八张卡片同时展示工期、进度条和完成数，挤占下方任务区，窄桌面还会翻倍占高。
 * 默认只保留阶段切换、完成数与异常提醒；工期明细按需展开。数据口径与校准权限仍由
 * 工作台和 PhaseCalibrate 管理，本组件只负责展示，不写入工程或付款数据。
 */
import React, { useState } from "react";
import { AlertTriangle, Banknote, Check, ChevronDown, CircleDot } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import "./TracePhaseBar.css";

/**
 * phases/timings 使用引擎原始快照；current 为实际阶段，selected 为用户正在查看的阶段。
 * onSelect(idx) 交回工作台切换任务；onOpenPayment(item) 沿用原付款跳转。
 * calibration 接收既有校准操作，payPendingByPhase 保留按工种分组的待付款提醒与 testid。
 * 返回默认收起工期的阶段导航，折叠仅影响次要明细，异常和付款入口始终可见。
 */
export function TracePhaseBar({ phases = [], timings = [], current, selected, zh, onSelect, calibration, payPendingByPhase = {}, onOpenPayment }) {
  const [expanded, setExpanded] = useState(false);
  const currentPhase = phases.find((phase) => phase.idx === current);
  const currentTiming = timings[current] || {};
  const phaseName = (phase) => zh ? phase.zh : phase.en;

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded} className="trace-phase-bar min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid="trace-ws-phase-bar">
      <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="text-xs font-semibold text-slate-900">{zh ? "全链路运行状态" : "Chain status"}</p>
        <p className="flex min-w-0 flex-wrap items-center gap-1 text-xs text-slate-500">
          {zh ? "当前：" : "Current: "}
          <span className={`font-semibold ${currentTiming.overdue ? "text-red-600" : "text-brand-600"}`}>
            {currentPhase ? phaseName(currentPhase) : current >= 8 ? (zh ? "已竣工" : "Completed") : "—"}
          </span>
        </p>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {calibration}
          <CollapsibleTrigger asChild>
            <button type="button" data-testid="trace-ws-phase-details-toggle" className="inline-flex min-h-7 items-center gap-1 rounded-md px-2 text-xs text-slate-600 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
              {expanded ? (zh ? "收起工期" : "Hide durations") : (zh ? "展开工期" : "Show durations")}
              <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${expanded ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
        </div>
      </div>

      {/* 按内容区实际宽度排成八 / 四 / 两列，侧栏和大字号挤压空间时仍保留完整阶段名称。 */}
      <div className="trace-phase-grid gap-1">
        {phases.map((phase) => {
          const timing = timings[phase.idx] || {};
          const isCurrent = phase.idx === current;
          const isSelected = phase.idx === selected;
          const complete = phase.total > 0 && phase.done >= phase.total;
          const payments = payPendingByPhase[phase.idx] || [];
          return (
            <div key={phase.idx} className="flex min-w-0 flex-col">
              <button type="button" data-testid={`trace-ws-phase-${phase.idx}`} aria-pressed={isSelected} aria-current={isCurrent ? "step" : undefined}
                onClick={() => onSelect(phase.idx)}
                className={`flex min-h-9 w-full flex-1 flex-col justify-center gap-0.5 rounded-md border px-2 py-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${isSelected ? "border-brand-300 bg-brand-50" : "border-transparent hover:bg-slate-50"}`}>
                <span className="flex min-w-0 items-center gap-1 text-xs font-semibold text-slate-700">
                  {isCurrent ? <CircleDot aria-hidden="true" className="h-3 w-3 shrink-0 text-brand-500" /> : complete ? <Check aria-hidden="true" className="h-3 w-3 shrink-0 text-emerald-600" /> : null}
                  <span className="min-w-0 break-words">{phaseName(phase)}</span>
                </span>
                <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-4 text-slate-500">
                  <span className="tabular-nums"><span className="sr-only">{zh ? "已完成任务 " : "Tasks completed "}</span>{phase.done}/{phase.total}</span>
                  {timing.overdue && <span className="inline-flex items-center gap-0.5 font-semibold text-red-600"><AlertTriangle aria-hidden="true" className="h-3 w-3 shrink-0" />{zh ? "超期" : "Overdue"}</span>}
                  {!!phase.blocked && <span className="font-semibold text-red-600">{phase.blocked} {zh ? "阻塞" : "blocked"}</span>}
                </span>
              </button>
              {/* 付款是独立操作，不能嵌套在阶段按钮里；保留原跳转参数，并让 Enter / Space 都能触发。 */}
              {payments.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1" data-testid={`trace-ws-phase-paypending-${phase.idx}`} data-count={payments.reduce((sum, item) => sum + item.count, 0)}>
                  {payments.map((item) => (
                    <button type="button" key={item.trade} data-testid={`trace-ws-phase-paypending-${phase.idx}-${item.trade}`} data-count={item.count}
                      title={zh ? `${item.name}：${item.count} 个付款申请等你处理 → 打开分包卡付款区` : `${item.name}: ${item.count} payment request(s) for you`}
                      onClick={() => onOpenPayment(item)}
                      className="inline-flex min-h-6 max-w-full items-center gap-1 rounded-md bg-red-600 px-1.5 text-[11px] font-semibold text-white hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                      <Banknote aria-hidden="true" className="h-3 w-3 shrink-0" /><span className="min-w-0 break-words">{item.name} · {item.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <CollapsibleContent data-testid="trace-ws-phase-details">
        <div className="trace-phase-grid mt-2 gap-x-3 gap-y-2 border-t border-slate-200 pt-2">
          {phases.map((phase) => {
            const timing = timings[phase.idx] || {};
            const percent = phase.total ? Math.round((phase.done / phase.total) * 100) : 0;
            return (
              <div key={phase.idx} className="min-w-0 text-xs">
                <p className="break-words font-medium text-slate-700">{phaseName(phase)}</p>
                <p className={`mt-0.5 leading-5 ${timing.overdue ? "font-semibold text-red-600" : "text-slate-500"}`} data-testid={`trace-ws-phase-days-${phase.idx}`}>
                  {zh ? "实际" : "Act"} {timing.actualDays != null ? `${timing.actualDays}${zh ? "天" : "d"}` : "—"} / {zh ? "计划" : "Plan"} {timing.planned ?? "—"}{zh ? "天" : "d"}
                </p>
                <div aria-hidden="true" className="mt-1 h-1 overflow-hidden rounded-full bg-slate-200"><div className={`h-full ${timing.overdue ? "bg-red-500" : percent === 100 ? "bg-emerald-500" : "bg-brand-500"}`} style={{ width: `${percent}%` }} /></div>
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
